import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Replace, Sparkles, Loader2, Copy, X, Info, Crown, Search, ArrowRight, Star, ShieldAlert, Lightbulb, ShoppingBasket,
} from "lucide-react";

const PANTRY_SUGGESTIONS = [
  "milk", "eggs", "butter", "flour", "sugar", "salt", "baking powder", "baking soda",
  "yogurt", "sour cream", "cream", "lemon", "vinegar", "olive oil", "vegetable oil",
  "honey", "maple syrup", "cornstarch", "almond flour", "vanilla",
];

export default function IngredientSubstitute() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [ingredient, setIngredient] = useState("");
  const [amount, setAmount] = useState("");
  const [context, setContext] = useState("");
  const [pantry, setPantry] = useState([]);
  const [pantryInput, setPantryInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    api.get("/substitute/quota").then((r) => setQuota(r.data));
  }, [user, loading, nav]);

  const addPantry = (val) => {
    const v = (val || pantryInput).trim();
    if (!v) return;
    if (pantry.map((p) => p.toLowerCase()).includes(v.toLowerCase())) { setPantryInput(""); return; }
    setPantry([...pantry, v]);
    setPantryInput("");
  };
  const removePantry = (v) => setPantry(pantry.filter((p) => p !== v));

  const find = async () => {
    if (ingredient.trim().length < 2) { toast.error("What ingredient do you want to substitute?"); return; }
    if (quota && !quota.is_premium && quota.remaining <= 0) { toast.error("Free tier limit reached. Upgrade to premium for unlimited."); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/substitute/find", {
        ingredient: ingredient.trim(),
        amount: amount || null,
        context: context || null,
        pantry,
      }, { timeout: 60000 });
      setResult(data);
      api.get("/substitute/quota").then((r) => setQuota(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyResult = () => {
    if (!result?.result) return;
    const r = result.result;
    const lines = [];
    lines.push(`Substitute for: ${result.amount ? result.amount + " " : ""}${result.ingredient}`);
    lines.push("");
    if (r.best_match) {
      lines.push("★ BEST MATCH");
      lines.push(`${r.best_match.name} — ${r.best_match.ratio}`);
      if (r.best_match.why) lines.push(`Why: ${r.best_match.why}`);
      if (r.best_match.impact) lines.push(`Impact: ${r.best_match.impact}`);
    }
    if (r.alternatives?.length) {
      lines.push("");
      lines.push("Alternatives:");
      for (const a of r.alternatives) {
        lines.push(`• ${a.name} — ${a.ratio}${a.best_for ? ` (best for ${a.best_for})` : ""}`);
      }
    }
    if (r.avoid?.length) {
      lines.push("");
      lines.push("Avoid:");
      for (const w of r.avoid) lines.push(`⚠ ${w}`);
    }
    if (r.pro_tip) {
      lines.push("");
      lines.push(`Pro tip: ${r.pro_tip}`);
    }
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Copied");
  };

  if (loading || !user) return null;
  const r = result?.result;
  const remaining = quota?.is_premium ? "unlimited" : `${quota?.remaining ?? "…"} of ${quota?.limit ?? 10}`;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Replace className="w-3.5 h-3.5" style={{ color: "#E8842B" }} />
              <span>Baker's tool</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Ingredient <span style={{ color: "#E8842B" }}>Substitution</span> Guide
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">
              Out of buttermilk? No eggs? Tell us what you're replacing (and optionally what's in your pantry) — we'll suggest the best swap and 3–5 alternatives with exact ratios.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-widest text-[#857163]">This month</div>
            <div className="font-heading text-2xl text-[#3B2C24] mt-0.5">
              {quota?.is_premium ? <span className="inline-flex items-center gap-1"><Crown className="w-5 h-5" style={{ color: "#F4A83B" }} /> Unlimited</span> : remaining}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
          {/* Left: query form */}
          <div className="cp-card p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-[110px_1fr] gap-2">
              <label>
                <span className="text-xs text-[#857163]">Amount (optional)</span>
                <input
                  className="cp-input mt-1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="1 cup"
                  data-testid="amount-input"
                />
              </label>
              <label>
                <span className="text-xs text-[#857163]">Ingredient to substitute *</span>
                <input
                  className="cp-input mt-1"
                  value={ingredient}
                  onChange={(e) => setIngredient(e.target.value)}
                  placeholder="buttermilk"
                  onKeyDown={(e) => e.key === "Enter" && find()}
                  data-testid="ingredient-input"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs text-[#857163]">What are you making? (optional)</span>
              <input
                className="cp-input mt-1"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="pancakes, brownies, sourdough loaf…"
                data-testid="context-input"
              />
            </label>

            <div>
              <div className="flex items-center gap-1.5 text-xs text-[#857163] mb-1.5">
                <ShoppingBasket className="w-3.5 h-3.5" style={{ color: "#E8842B" }} />
                <span>Pantry — what do you have on hand? (optional but recommended)</span>
              </div>
              <div className="cp-input flex flex-wrap items-center gap-1.5 py-2 min-h-[46px] cursor-text" onClick={() => document.getElementById("pantry-input")?.focus()}>
                {pantry.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#F9EFE6] border border-[#EBDCD1]" data-testid={`pantry-chip-${p}`}>
                    {p}
                    <button onClick={() => removePantry(p)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                <input
                  id="pantry-input"
                  className="flex-1 min-w-[100px] outline-none bg-transparent text-sm"
                  value={pantryInput}
                  onChange={(e) => setPantryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addPantry(); }
                    if (e.key === "Backspace" && !pantryInput && pantry.length) removePantry(pantry[pantry.length - 1]);
                  }}
                  placeholder={pantry.length ? "" : "milk, eggs, butter…"}
                  data-testid="pantry-input"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {PANTRY_SUGGESTIONS.filter((s) => !pantry.map((p) => p.toLowerCase()).includes(s)).slice(0, 10).map((s) => (
                  <button
                    key={s}
                    onClick={() => addPantry(s)}
                    className="px-2 py-1 rounded-full text-[11px] border border-dashed border-[#EBDCD1] text-[#857163] hover:bg-[#F9EFE6]"
                    data-testid={`pantry-suggest-${s}`}
                  >+ {s}</button>
                ))}
              </div>
            </div>

            <button
              onClick={find}
              disabled={busy || ingredient.trim().length < 2 || (quota && !quota.is_premium && quota.remaining <= 0)}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold text-base transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #E8842B 0%, #C2185B 100%)" }}
              data-testid="find-btn"
            >
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Searching…</> : <><Search className="w-5 h-5" /> Find substitution</>}
            </button>

            <div className="text-[11px] text-[#857163] leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Adding pantry items lets the AI prioritize what you already have. Ratios are approximate — always taste-test.</span>
            </div>
          </div>

          {/* Right: results */}
          <div className="cp-card p-5 sm:p-6 min-h-[400px]" data-testid="results-panel">
            {!result && !busy && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 rounded-full grid place-items-center mb-4" style={{ background: "rgba(232,132,43,0.10)" }}>
                  <Replace className="w-7 h-7" style={{ color: "#E8842B" }} />
                </div>
                <div className="font-heading text-2xl text-[#3B2C24]">Ask me anything</div>
                <p className="text-sm text-[#857163] mt-2 max-w-sm">
                  Try: "buttermilk", "1 stick butter", "2 eggs" — with or without a pantry.
                </p>
              </div>
            )}
            {busy && (
              <div className="h-full flex flex-col items-center justify-center py-16">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: "#E8842B" }} />
                <div className="mt-4 font-heading text-lg text-[#3B2C24]">Checking swaps…</div>
              </div>
            )}
            {r && (
              <div className="space-y-4" data-testid="subs-result">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs uppercase tracking-widest text-[#857163]">
                    Substituting: <b className="text-[#3B2C24]">{result.amount ? result.amount + " " : ""}{r.target || result.ingredient}</b>
                  </div>
                  <button
                    onClick={copyResult}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white hover:opacity-90 transition"
                    style={{ background: "#E8842B" }}
                    data-testid="copy-btn"
                  ><Copy className="w-3 h-3" /> Copy</button>
                </div>

                {r.best_match && (
                  <div className="p-4 rounded-2xl border-2" style={{ borderColor: "#E8842B", background: "rgba(232,132,43,0.06)" }} data-testid="best-match">
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: "#E8842B" }}>
                        <Star className="w-3.5 h-3.5 fill-current" /> Best match
                      </div>
                      {r.best_match.from_pantry && (
                        <span className="cp-tag" style={{ background: "rgba(76,159,112,0.20)", color: "#2F6A47" }}>From your pantry ✓</span>
                      )}
                    </div>
                    <div className="font-heading text-xl text-[#3B2C24]">{r.best_match.name}</div>
                    <div className="font-mono text-sm text-[#3B2C24] mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-[#857163]">{result.ingredient}</span>
                      <ArrowRight className="w-3.5 h-3.5" style={{ color: "#E8842B" }} />
                      <span className="font-semibold">{r.best_match.ratio}</span>
                    </div>
                    {r.best_match.why && <div className="text-xs text-[#857163] mt-2 italic">↳ {r.best_match.why}</div>}
                    {r.best_match.impact && <div className="text-xs text-[#3B2C24] mt-1">Impact: <span className="text-[#857163]">{r.best_match.impact}</span></div>}
                  </div>
                )}

                {r.alternatives?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-2">Alternatives</div>
                    <div className="space-y-2">
                      {r.alternatives.map((a, i) => (
                        <div key={i} className="p-3 rounded-2xl border border-[#EBDCD1]" data-testid={`alt-${i}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="font-semibold text-[#3B2C24]">{a.name}</div>
                            {a.from_pantry && (
                              <span className="cp-tag text-[10px]" style={{ background: "rgba(76,159,112,0.15)", color: "#2F6A47" }}>Pantry ✓</span>
                            )}
                          </div>
                          <div className="font-mono text-xs text-[#3B2C24] mt-1">{a.ratio}</div>
                          {a.best_for && <div className="text-[11px] text-[#857163] mt-1">Best for: {a.best_for}</div>}
                          {a.notes && <div className="text-[11px] text-[#857163] mt-0.5 italic">{a.notes}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.avoid?.length > 0 && (
                  <div className="p-3 rounded-2xl border-l-4 border-[#C2185B] bg-[#C2185B]/5">
                    <div className="text-sm font-semibold text-[#3B2C24] mb-1 inline-flex items-center gap-1.5">
                      <ShieldAlert className="w-4 h-4" style={{ color: "#C2185B" }} /> Avoid
                    </div>
                    <ul className="space-y-0.5">
                      {r.avoid.map((w, i) => <li key={i} className="text-sm text-[#3B2C24] flex gap-2"><span style={{ color: "#C2185B" }}>✗</span>{w}</li>)}
                    </ul>
                  </div>
                )}

                {r.pro_tip && (
                  <div className="p-3 rounded-2xl flex items-start gap-2" style={{ background: "rgba(244,168,59,0.10)" }}>
                    <Lightbulb className="w-5 h-5 shrink-0" style={{ color: "#F4A83B" }} />
                    <div className="text-sm text-[#3B2C24]"><b>Pro tip:</b> {r.pro_tip}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
